import { type ReactNode, useState } from "react";

import { EmptyState } from "./EmptyState";
import { FormInput } from "./forms";
import { Text } from "./Text";

export interface FilterableListProps<T> {
  /**
   * Array of items to display
   */
  items: T[];

  /**
   * Function to filter items based on the search query
   */
  filterPredicate: (item: T, query: string) => boolean;

  /**
   * Function to render each item
   */
  renderItem: (item: T, isSelected: boolean) => ReactNode;

  /**
   * Optional callback when an item is selected (if selection is managed internally or for tracking)
   */
  onSelect?: (item: T) => void;

  /**
   * Currently selected item ID or key (for controlled selection styling)
   */
  selectedId?: string | number | null;

  /**
   * Key in the item object that uniquely identifies it (used for comparison with selectedId)
   */
  keyExtractor: (item: T) => string | number;

  /**
   * Placeholder text for the search input
   */
  searchPlaceholder?: string;

  /**
   * Max height of the scrollable list area
   * @default 'max-h-96'
   */
  maxHeight?: string;

  /**
   * Whether data is loading
   */
  loading?: boolean;

  /**
   * Title for the empty state
   */
  emptyTitle?: string;

  /**
   * Description for the empty state
   */
  emptyDescription?: string;

  /**
   * Optional content to render before the list (e.g. a "Global" or "All" option)
   */
  headerItem?: ReactNode;
}

/**
 * FilterableList Component
 *
 * A reusable component that provides a search input and a scrollable list of items.
 * Handles filtering logic and empty states.
 */
export function FilterableList<T>({
  items,
  filterPredicate,
  renderItem,
  keyExtractor,
  selectedId,
  searchPlaceholder = "Filter items...",
  maxHeight = "max-h-96",
  loading = false,
  emptyTitle = "No items found",
  emptyDescription = "Try adjusting your filter.",
  headerItem,
}: FilterableListProps<T>) {
  const [filter, setFilter] = useState("");

  const filteredItems = items.filter((item) => filterPredicate(item, filter));

  return (
    <div className="flex flex-col gap-3">
      <FormInput
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={searchPlaceholder}
        className="text-sm"
      />

      <div className="flex flex-col gap-1.5">
        {headerItem}

        {headerItem && (
          <div className="my-1 h-px bg-gray-200 dark:bg-white/10" />
        )}

        {loading && <Text className="px-1">Loading...</Text>}

        {!loading && filteredItems.length === 0 && (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            className="py-2"
            size="sm"
          />
        )}

        <div
          className={`custom-scrollbar flex flex-col gap-1.5 overflow-y-auto ${maxHeight} pr-0.5`}
        >
          {filteredItems.map((item) => {
            const key = keyExtractor(item);
            const isSelected = selectedId === key;
            return <div key={key}>{renderItem(item, isSelected)}</div>;
          })}
        </div>
      </div>
    </div>
  );
}
